/**
 * What a file chosen as evidence has to be, judged the moment it is chosen.
 *
 * <p><b>The server is the gate and this is a copy of its rule</b> (AuditEvidence, in the
 * domain). It exists because the server can only say no after the upload: a HEIC photograph
 * from a Samsung gallery, or a two-minute video, went into the queue, showed on screen, was
 * sent over mobile data, came back refused, vanished from the question — and the refusal was
 * printed at the top of the page while the auditor was looking at question forty. Judged
 * here, the same words appear at once, beside the question, and nothing is sent.</p>
 *
 * <p>Not the accept attribute. Narrowing that makes Android's picker <i>convert</i> the
 * chosen item instead of hiding it (see Audit.tsx), so the picker stays wide and the file
 * is judged after it has been handed over.</p>
 *
 * <p>A copy drifts, so evidence.test.ts reads AuditEvidence.cs and fails when the two
 * disagree.</p>
 */

export const PhotoTypes: readonly string[] = ['image/jpeg', 'image/png', 'image/webp'];

export const VideoTypes: readonly string[] = ['video/mp4', 'video/quicktime', 'video/webm'];

export const MaximumPhotoBytes = 15 * 1024 * 1024;

export const MaximumVideoBytes = 100 * 1024 * 1024;

/**
 * Types worked out from the name, for a file that arrives without one.
 *
 * <p>Some Android file managers hand over a file whose type is empty. Sent as it is, the
 * server can only refuse it as "'' is not a kind of evidence" — a sentence about nothing.
 * HEIC is here so that it is refused by name rather than as an unknown.</p>
 */
const ByExtension: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};

/**
 * A type no file has, added to a gallery's accept list on Android so that Chrome does not
 * open the system Photo Picker.
 *
 * <p><b>Files from the Photo Picker could not be read, some of the time.</b> On a phone the
 * screen said "the phone could not read the file (TypeError: network error)" for gallery
 * photographs and videos, while the camera never failed. Chrome on Android opens the Photo
 * Picker whenever every accepted type is an image or a video (SelectFileDialog,
 * isSupportedPhotoPickerTypes), and the picker hands over a stand-in for the file whose size
 * comes from its own index rather than from the bytes. When the two disagree Chrome refuses
 * every read of it. Which items disagree is the picker's business, so the failure was
 * "sometimes", and the files arrived under the picker's numeric names (145877.jpg) rather
 * than their own.</p>
 *
 * <p>One type that is neither image nor video and Chrome shows its ordinary chooser
 * instead, which hands over the file itself. The same picker is the likeliest source of the
 * substituted still image the wide accept list was put back for (ddbd91a). The cameras are
 * left alone: a capture request never reaches the picker, and the camera did not fail.</p>
 *
 * <p>Android only. Safari reads the accept list to decide what to offer and whether to
 * convert HEIC, and nothing here is wrong there.</p>
 */
export const NotMedia = 'application/x-norva-evidence';

/** The accept list for a gallery picker on this device. See {@link NotMedia}. */
export function galleryAccept(types: string, userAgent: string): string {
  return /Android/i.test(userAgent) ? `${types},${NotMedia}` : types;
}

/**
 * The file's bytes, read now, as a blob of the page's own.
 *
 * <p>A file from Android's picker is a reference to the phone's storage, read only when
 * something asks for its bytes — and the database asks at commit, a moment after the picker
 * has closed. A video failed exactly there on a phone, and photographs failed after it.
 * Read while the picker's grant is fresh, the bytes are the
 * page's own and the database never has to reach back into the gallery. A file that cannot
 * be read fails here, with its own error, before anything is queued.</p>
 *
 * <p>In pieces, each copied into a blob as it arrives, rather than as one ArrayBuffer: a
 * hundred-megabyte video held whole in memory, and then copied, is twice that on a phone
 * that may not have it.</p>
 */
export async function copyOf(file: Blob, type: string): Promise<Blob> {
  const parts: Blob[] = [];
  const reader = file.stream().getReader();

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(new Blob([value]));
  }

  return new Blob(parts, { type });
}

/** An error's own name and words, for the line that says why a file did not make it. */
export function technical(error: unknown): string {
  const shaped = error as { name?: string; message?: string } | null;
  return shaped?.name ? `(${shaped.name}${shaped.message ? `: ${shaped.message}` : ''})` : `(${String(error)})`;
}

export type Verdict =
  | { ok: true; type: string }
  | { ok: false; code: string; args: Record<string, string> };

/** The media type without its parameters, as the server compares it. */
const kind = (type: string) => type.split(';')[0]!.trim().toLowerCase();

function typeOf(file: { name: string; type: string }): string {
  if (file.type) return file.type;

  const dot = file.name.lastIndexOf('.');
  return dot < 0 ? '' : (ByExtension[file.name.slice(dot + 1).toLowerCase()] ?? '');
}

/**
 * Whether the server would take this file, and the type to send it under.
 *
 * <p>The refusals carry the server's own error codes and arguments, so they read exactly
 * as the server's would — one set of words, in both languages, for one rule.</p>
 */
export function judge(file: { name: string; type: string; size: number }): Verdict {
  // In the server's order: empty, then the kind, then the size the kind allows.
  if (file.size <= 0) return { ok: false, code: 'audit.photo.empty', args: {} };

  const type = typeOf(file);
  const limit = VideoTypes.includes(kind(type))
    ? MaximumVideoBytes
    : PhotoTypes.includes(kind(type))
      ? MaximumPhotoBytes
      : null;

  if (limit === null) {
    return { ok: false, code: 'audit.photo.type', args: { type: type || file.name } };
  }

  if (file.size > limit) {
    return { ok: false, code: 'audit.photo.too-large', args: { limit: String(limit / 1024 / 1024) } };
  }

  return { ok: true, type };
}
